import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Select,
	SelectChangeEvent,
} from '@mui/material'
import { action } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useMemo } from 'react'
import {
	BaseTandemFieldInstanceModel,
	TandemFieldConfiguration,
} from '../../api/clients/tandem/DTOs'
import {
	FormBuilderViewer,
	StandaloneCellManager,
} from '../../modules/FormBuilderInterop/Runtimes/Standalone'
import { FixedHeader } from '../../utils/HOC/FixedHeaders'

type AddTandemFieldDialogProps = {
	availableFields: TandemFieldConfiguration[]
	onConfirm: (v: BaseTandemFieldInstanceModel) => void
	onCancel: () => void
}

export const AddTandemFieldDialog = observer(
	(props: AddTandemFieldDialogProps) => {
		const localStore = useLocalObservable(() => ({
			selectedField:
				props.availableFields.length > 0 ? props.availableFields[0] : undefined,
			fieldProperties:
				props.availableFields.length > 0
					? props.availableFields[0].properties
					: undefined,
		}))

		const cellManager = useMemo(() => {
			if (localStore.selectedField !== undefined)
				return new StandaloneCellManager(
					localStore.selectedField.formBuilderSchema,
					localStore.fieldProperties,
				)
		}, [localStore.selectedField, localStore.fieldProperties])

		return (
			<>
				<DialogTitle>Add Field</DialogTitle>
				<Box
					component={DialogContent}
					display="flex"
					flexDirection="column"
					gap={2}
				>
					<FixedHeader label="Field">
						<Select
							value={localStore.selectedField?.fieldName}
							onChange={action(
								(evt: SelectChangeEvent) =>
									(localStore.selectedField = props.availableFields.find(
										(v) => v.fieldName === evt.target.value,
									)),
							)}
							fullWidth
						>
							{props.availableFields.map((field) => (
								<MenuItem value={field.fieldName}>{field.displayName}</MenuItem>
							))}
						</Select>
					</FixedHeader>
					{cellManager !== undefined && (
						<FormBuilderViewer cellManager={cellManager} />
					)}
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={props.onCancel}>
						Cancel
					</Button>
					<Button
						variant="contained"
						disabled={
							localStore.selectedField === undefined ||
							localStore.fieldProperties === undefined ||
							props.availableFields.length === 0
						}
						onClick={() => {
							if (
								localStore.fieldProperties === undefined ||
								localStore.selectedField === undefined
							)
								return

							props.onConfirm({
								fieldName: localStore.selectedField?.fieldName,
								properties: localStore.fieldProperties,
							})
						}}
					>
						Add Field
					</Button>
				</DialogActions>
			</>
		)
	},
)
